import { useEffect, useState } from 'react';
import { requester } from 'App';
import styles from "./VendorVerificationStats.module.css";

function VendorVerificationStats() {
    const [verificationStats, setVerificationStats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date());

    // 월 변경 함수
    const changeMonth = (amount) => {
        const newDate = new Date(selectedMonth);
        newDate.setMonth(newDate.getMonth() + amount);
        setSelectedMonth(newDate);
    };

    // 선택한 월 표시 형식
    const formatMonthYear = (date) => {
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
    };

    // 선택한 월의 시작일과 종료일 계산
    const calculateMonthRange = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-11
        
        // 선택한 월의 1일
        const startDate = new Date(year, month, 1);
        
        // 다음 달의 0일 (선택한 월의 마지막 날)
        const endDate = new Date(year, month + 1, 0);
        
        // 날짜 형식 변환 함수
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // 월은 0부터 시작하므로 +1
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            startDate: formatDate(startDate), // YYYY-MM-DD 형식
            endDate: formatDate(endDate)      // YYYY-MM-DD 형식
        };
    };

    // 선택한 월이 변경될 때마다 데이터 다시 불러오기
    useEffect(() => {
        setIsLoading(true);
        const dateRange = calculateMonthRange(selectedMonth);
        
        let data = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        };
        
        requester.vendorAdultVerificationStats(data, result => {
            if (result.data) {
                setVerificationStats(result.data);
            }
            setIsLoading(false);
        });
    }, [selectedMonth]);

    // 전체 통계 계산
    const calculateTotalStats = () => {
        if (!Array.isArray(verificationStats) || verificationStats.length === 0) {
            return {
                totalCount: 0,
                passCount: 0,
                smsCount: 0,
                appVerificationCount: 0,
                paymentVerificationCount: 0
            };
        }

        const totalCount = verificationStats.reduce((sum, store) => sum + store.totalCount, 0);
        const passCount = verificationStats.reduce((sum, store) => sum + store.passCount, 0);
        const smsCount = verificationStats.reduce((sum, store) => sum + store.smsCount, 0);
        const appVerificationCount = verificationStats.reduce((sum, store) => sum + (store.appVerificationCount || 0), 0);
        const paymentVerificationCount = verificationStats.reduce((sum, store) => sum + (store.paymentVerificationCount || 0), 0);
        
        return {
            totalCount,
            passCount,
            smsCount,
            appVerificationCount,
            paymentVerificationCount
        };
    };

    const totalStats = calculateTotalStats();

    return (
        <div className={styles.container}>
            <div className={styles.flexGrid}>
                {/* 성인인증 통계 현황 카드 */}
                <div className={`${styles.flexBox} ${styles.wideBox}`}>
                    <div className={styles.card}>
                        <div className={styles.section}>
                            <div className={styles.sectionContent}>
                                {/* 월 선택기 */}
                                <div className={styles.monthSelector}>
                                    <button 
                                        className={styles.monthBtn} 
                                        onClick={() => changeMonth(-1)}
                                        disabled={isLoading}
                                    >
                                        &lt; 이전 달
                                    </button>
                                    <span className={styles.currentMonth}>{formatMonthYear(selectedMonth)}</span>
                                    <button 
                                        className={styles.monthBtn} 
                                        onClick={() => changeMonth(1)}
                                        disabled={isLoading}
                                    >
                                        다음 달 &gt;
                                    </button>
                                </div>

                                {/* 로딩 상태 */}
                                {isLoading ? (
                                    <div className={styles.loading}>데이터를 불러오는 중...</div>
                                ) : verificationStats.length === 0 ? (
                                    <div className={styles.noData}>
                                        {formatMonthYear(selectedMonth)} 기간에 해당하는 데이터가 없습니다.
                                    </div>
                                ) : (
                                    <>
                                        {/* 전체 통계 요약 */}
                                        <div className={styles.statsSummary}>
                                            <div className={styles.summaryHeader}>
                                                <h3>{formatMonthYear(selectedMonth)} 전체 통계</h3>
                                            </div>
                                            <div className={styles.summaryContent}>
                                                <div className={styles.statRow}>
                                                    <div className={styles.statCol}>
                                                        <span className={styles.statLabel}>총 시도</span>
                                                        <span className={styles.statValue}>{totalStats.totalCount}</span>
                                                    </div>
                                                    <div className={styles.statCol}>
                                                        <span className={styles.statLabel}>PASS인증</span>
                                                        <span className={`${styles.statValue} ${styles.green}`}>{totalStats.passCount}</span>
                                                    </div>
                                                    <div className={styles.statCol}>
                                                        <span className={styles.statLabel}>SMS인증</span>
                                                        <span className={`${styles.statValue} ${styles.blue}`}>{totalStats.smsCount}</span>
                                                    </div>
                                                    <div className={styles.statCol}>
                                                        <span className={styles.statLabel}>앱인증</span>
                                                        <span className={`${styles.statValue} ${styles.purple}`}>{totalStats.appVerificationCount}</span>
                                                    </div>
                                                    <div className={styles.statCol}>
                                                        <span className={styles.statLabel}>결제인증</span>
                                                        <span className={`${styles.statValue} ${styles.orange}`}>{totalStats.paymentVerificationCount}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 매장별 통계 리스트 */}
                                        <div className={styles.storeStatsList}>
                                            <div className={styles.listHeader}>
                                                <h3>매장별 성인인증 통계</h3>
                                            </div>
                                            <div className={styles.listContent}>
                                                <table className={styles.statsTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>매장명</th>
                                                            <th>총 시도</th>
                                                            <th>PASS인증</th>
                                                            <th>SMS인증</th>
                                                            <th>앱인증</th>
                                                            <th>결제인증</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {verificationStats.map(store => (
                                                            <tr key={store.storeCode}>
                                                                <td className={styles.storeName}>
                                                                    <div>{store.storeName}</div>
                                                                    <div className={styles.storeCode}>코드: {store.storeCode}</div>
                                                                </td>
                                                                <td>{store.totalCount}</td>
                                                                <td className={styles.green}>{store.passCount}</td>
                                                                <td className={styles.blue}>{store.smsCount}</td>
                                                                <td className={styles.purple}>{store.appVerificationCount || 0}</td>
                                                                <td className={styles.orange}>{store.paymentVerificationCount || 0}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VendorVerificationStats; 